"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareValue = void 0;
const prepare_value_list_1 = __importDefault(require("./prepare-value-list"));
const prepare_exclusions_1 = __importDefault(require("./prepare-exclusions"));
const prepare_value_type_1 = require("./prepare-value-type");
const error_classes_1 = __importDefault(require("../error-classes"));
const object_properties_1 = require("../lib/object-properties");
const json_compare_1 = require("../lib/json-compare");
function prepareValue(value, lookups) {
    let dependencies = new Set();
    const preparedValueType = (0, prepare_value_type_1.prepareValueType)(value, lookups);
    for (const valueTypeItem of preparedValueType) {
        dependencies = new Set([...dependencies, ...valueTypeItem.dependencies]);
    }
    const list = (0, prepare_value_list_1.default)(value, lookups);
    const exclusions = (0, prepare_exclusions_1.default)(value, lookups);
    dependencies = new Set([
        ...dependencies,
        ...list.dependencies,
        ...exclusions.dependencies,
    ]);
    const evalFn = (state, errors) => {
        const result = [];
        const length = list.getLength(state);
        const numItems = (length !== null && length !== void 0 ? length : 1);
        const excludedValues = exclusions.getExclusions(state).filter((v) => v !== undefined);
        const missingDeps = [...dependencies].filter((dep) => {
            const testState = Object.assign(Object.assign({}, state), { listIndex: length, _i: length });
            const prop = (0, object_properties_1.getObjectProperty)(testState, dep, {});
            return prop === undefined || prop === null;
        });
        const errorDeps = missingDeps.filter((dep) => errors.includes(dep));
        const unknownDeps = missingDeps.filter((dep) => !errors.includes(dep));
        const depErrors = [];
        if (errorDeps.length) {
            const depString = errorDeps.map((d) => `'${d}'`).join(', ');
            depErrors.push(`This value depends on values with errors: ${depString}`);
        }
        if (unknownDeps.length) {
            const depString = unknownDeps.map((d) => `'${d}'`).join(', ');
            depErrors.push(`This value has missing dependencies: ${depString}`);
        }
        if (depErrors.length)
            throw new error_classes_1.default.DependencyError(depErrors.join('~§~'));
        // If a list of length 0 has been specified, return an empty array.
        if (numItems === 0)
            return [];
        // Sometimes exclusions and Set restrictions make it harder to populate
        // a list with the required number of items. So we try multiple times.
        let outerAttempts = 100;
        while (outerAttempts && (result.length < (numItems))) {
            for (let i = 0; i < numItems && result.length < numItems; i++) {
                let isAdded = false;
                const attempt = getValueAvoidingExclusions(preparedValueType, state, excludedValues.concat(list.isSet ? result : []), i);
                if (attempt !== null) {
                    result.push(attempt);
                    isAdded = true;
                }
                if (!isAdded)
                    break;
            }
            outerAttempts--;
        }
        if (result.length < numItems)
            throw new error_classes_1.default.EvaluationError(`The '${value.name}' value could not be evaluated.`);
        return length ? result : result[0];
    };
    return {
        name: value.name,
        type: 'function',
        dependencies,
        fn: evalFn,
    };
}
exports.prepareValue = prepareValue;
exports.default = prepareValue;
// Keep generating values until we get one that isn't excluded.
// If the maximum number of attempts is exceeded, return null.
function getValueAvoidingExclusions(preparedValueType, state, excludedValues = [], listIndex) {
    let numAttempts = excludedValues.length ? 1000 : 1;
    let value = null;
    while (numAttempts && !value) {
        const attempt = getAttempt(preparedValueType, state, listIndex);
        if (!excludedValues.some((v) => (0, json_compare_1.jsonCompare)(attempt, v)))
            value = attempt;
        numAttempts--;
    }
    return value;
}
// Generate a value using the prepared evaluate functions for each item.
// Merge multiple items into an object to return.
function getAttempt(preparedValueType, state, listIndex) {
    const valueTypeItems = preparedValueType
        .map((valueTypeItem) => [
        valueTypeItem.name,
        valueTypeItem.evaluate(Object.assign(Object.assign({ listIndex }, state), { _i: listIndex }))
    ]);
    return valueTypeItems.length === 1
        ? valueTypeItems[0][1]
        : valueTypeItems.reduce((soFar, val) => (0, object_properties_1.setObjectProperty)(soFar, val[0], val[1]), {});
}
